import { type RatingBoard, type RatingDto } from '@spin/shared';
import { useEffect, useState } from 'react';
import { http } from '../api/http';
import { Avatar, Spinner } from '../components/common';
import { Emoji } from '../components/Emoji';
import { CloseIcon } from '../components/icons';
import { reportError, useT } from '../i18n';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';

/** The reference's five boards, in its order. */
export const BOARDS: readonly { id: RatingBoard; icon: string }[] = [
  { id: 'kisses', icon: '💋' },
  { id: 'dj', icon: '🎵' },
  { id: 'expensive', icon: '💎' },
  { id: 'important', icon: '💕' },
  { id: 'emotional', icon: '😊' },
];

const PERIODS = ['all', 'month', 'week', 'day'] as const;

/** Ratings: five boards behind icon tabs, a period picker, the top players and my own place. */
export function RatingsDialog() {
  const t = useT();
  const close = useUi.getState().closeDialog;
  const [board, setBoard] = useState<RatingBoard>('kisses');
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>('all');
  const [limit, setLimit] = useState<10 | 25>(10);
  const [help, setHelp] = useState(false);
  const key = `${board}:${board === 'expensive' ? 'all' : period}`;
  const [loaded, setLoaded] = useState<{ key: string; value: RatingDto } | null>(null);
  const data = loaded?.key === key ? loaded.value : null;

  useEffect(() => tg.backButton.show(close), [close]);
  useEffect(() => {
    const [b, p] = key.split(':');
    http
      .get<RatingDto>(`/ratings/${b}?period=${p}`)
      .then((value) => setLoaded({ key, value }))
      .catch(reportError);
  }, [key]);

  const icon = BOARDS.find((b) => b.id === board)!.icon;
  return (
    <div className="league-layer" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="ratings" role="dialog" data-testid="ratings-dialog">
        <button
          className="league-close"
          onClick={close}
          aria-label={t('common.close')}
          data-testid="dialog-close"
        >
          <CloseIcon />
        </button>
        <div className="ratings-tabs">
          <button
            className="ratings-help"
            onClick={() => setHelp((h) => !h)}
            aria-label={t('ratings.help_title')}
          >
            ?
          </button>
          {BOARDS.map((b) => (
            <button
              key={b.id}
              className={board === b.id ? 'on' : ''}
              onClick={() => setBoard(b.id)}
              aria-label={t(`ratings.${b.id}`)}
              title={t(`ratings.${b.id}`)}
              data-testid={`board-${b.id}`}
            >
              <Emoji e={b.icon} />
            </button>
          ))}
        </div>
        <div className="ratings-head">
          <b>{t(`ratings.${board}`)}</b>
          {board !== 'expensive' && (
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as (typeof PERIODS)[number])}
              aria-label={t('ratings.period')}
            >
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {t(`ratings.period_${p}`)}
                </option>
              ))}
            </select>
          )}
        </div>
        {help && <p className="ratings-help-text">{t(`ratings.help.${board}`)}</p>}
        <div className="ratings-list" data-testid="ratings">
          {!data ? (
            <Spinner />
          ) : data.top.length === 0 ? (
            <p className="league-empty">{t('ratings.empty')}</p>
          ) : (
            data.top.slice(0, limit).map((e) => (
              <div key={e.userId} className="rating-row">
                <Avatar name={e.displayName} photoUrl={e.photoUrl} size={36} />
                <span className="ellipsis">
                  {e.rank}. {e.displayName}
                </span>
                <span className="rating-score">
                  <span aria-hidden>
                    <Emoji e={icon} />
                  </span>
                  {e.score.toLocaleString('ru-RU')}
                </span>
              </div>
            ))
          )}
        </div>
        <div className="ratings-links">
          <button className={limit === 10 ? 'on' : ''} onClick={() => setLimit(10)}>
            {t('ratings.top_n', { n: 10 })}
          </button>
          <button className={limit === 25 ? 'on' : ''} onClick={() => setLimit(25)}>
            {t('ratings.top_n', { n: 25 })}
          </button>
        </div>
        {data?.me && (
          <div className="rating-row mine" data-testid="my-rating">
            <Avatar name={data.me.displayName} photoUrl={data.me.photoUrl} size={36} />
            <span className="ellipsis">
              {data.me.rank}. {data.me.displayName}
            </span>
            <span className="rating-score">
              <span aria-hidden>
                <Emoji e={icon} />
              </span>
              {data.me.score.toLocaleString('ru-RU')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
